import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// Cache wyników agregacji per-miasto, żeby nie odpytywać bazy przy każdym
// renderze formularza ani przy każdym wejściu na stronę. TTL 5 min daje
// świeże dane, a jednocześnie chroni bazę przed zalewem zapytań.
const cache = new Map<string, { at: number; codes: string[] }>();
const TTL_MS = 5 * 60_000;

/**
 * Zwraca uporządkowaną listę kodów IATA destynacji najczęściej WYSZUKIWANYCH
 * z podanego miasta wylotu (tabela `search_queries`, ostatnie 90 dni).
 * Używane jako sygnał popytu — kierunki, których realnie szukają użytkownicy
 * (np. WAW ze SZZ), trafiają wysoko do podpowiedzi w pickerze "Dokąd".
 *
 * Próg `>= 2` wyszukiwań eliminuje pojedyncze przypadkowe wpisy.
 */
export const getSearchedDestinations = createServerFn({ method: "GET" })
  .inputValidator((d: { city: string }) => ({
    city: String((d as { city?: string })?.city ?? "").toUpperCase(),
  }))
  .handler(async ({ data }) => {
    const city = data.city;
    if (!city) return { codes: [] as string[] };

    const cached = cache.get(city);
    if (cached && Date.now() - cached.at < TTL_MS) {
      return { codes: cached.codes };
    }

    const since = new Date(Date.now() - 90 * 86_400_000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("search_queries")
      .select("to_iata")
      .eq("from_iata", city)
      .not("to_iata", "is", null)
      .gte("created_at", since)
      .limit(5000);

    if (error || !rows) {
      cache.set(city, { at: Date.now(), codes: [] });
      return { codes: [] as string[] };
    }

    const counts = new Map<string, number>();
    for (const r of rows) {
      const t = String(r.to_iata ?? "").toUpperCase();
      if (!t || t === city) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    const codes = [...counts.entries()]
      .filter(([, c]) => c >= 2)
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);

    cache.set(city, { at: Date.now(), codes });
    return { codes };
  });
